import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlbumCard } from "@/components/cards/AlbumCard";
import { ArtistCard } from "@/components/cards/ArtistCard";
import { PlaylistCard } from "@/components/cards/PlaylistCard";
import { SectionShelf } from "@/components/shelf/SectionShelf";
import { Modal } from "@/components/ui/Modal";
import { useLibrary } from "@/hooks/useLibrary";
import { musicService } from "@/services/musicService";
import type { Playlist } from "@/types/types";

export function LibraryPage() {
  const { likedAlbums, likedArtists } = useLibrary();
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nav = useNavigate();

  const refresh = useCallback(async () => {
    try { const p = await musicService.playlists(); setPlaylists(p.items); }
    catch (e) { console.error(e); }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const submit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { setError("Le nom ne peut pas être vide"); return; }
    setSaving(true); setError(null);
    try {
      const created = await musicService.createPlaylist(trimmed, isPublic);
      setCreating(false); setName(""); setIsPublic(false);
      await refresh();
      if (created?.id) nav(`/playlist/${created.id}`);
    } catch (err) {
      console.error(err);
      setError("Impossible de créer la playlist");
    } finally { setSaving(false); }
  }, [name, isPublic, refresh, nav]);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>Ma bibliothèque</h1>
        <button className="btn primary" onClick={() => setCreating(true)}>+ Nouvelle playlist</button>
      </div>

      <SectionShelf title="Playlists">
        {playlists.length === 0 ? (
          <div className="muted" style={{ padding: "20px 0" }}>
            Aucune playlist. Clique <b>+ Nouvelle playlist</b> pour en créer une.
          </div>
        ) : playlists.map((p) => (
          <PlaylistCard key={p.id} playlist={p}
                        onDeleted={(id) => setPlaylists((cur) => cur.filter((x) => x.id !== id))} />
        ))}
      </SectionShelf>

      <SectionShelf title="Albums aimés">
        {likedAlbums.length === 0
          ? <div className="muted" style={{ padding: "20px 0" }}>Rien encore. Aime des albums pour les retrouver ici.</div>
          : likedAlbums.map((a) => <AlbumCard key={a.id} album={a} />)}
      </SectionShelf>

      <SectionShelf title="Artistes suivis">
        {likedArtists.length === 0
          ? <div className="muted" style={{ padding: "20px 0" }}>Pas d'artiste suivi. Va sur une fiche artiste et clique <i>Suivre</i>.</div>
          : likedArtists.map((a) => <ArtistCard key={a.id} artist={a} />)}
      </SectionShelf>

      <Modal open={creating} onClose={() => { if (!saving) { setCreating(false); setError(null); } }}
             title="Nouvelle playlist">
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span className="muted" style={{ fontSize: 13 }}>Nom</span>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder="Ma nouvelle playlist"
              style={{
                background: "var(--bg-elev-2)", border: "1px solid var(--line)",
                color: "var(--fg)", padding: "10px 12px", borderRadius: 8, fontSize: 15,
              }}
            />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
            <span>Publique (visible par les autres users)</span>
          </label>
          {error && <div style={{ color: "#f87171", fontSize: 13 }}>{error}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
            <button type="button" className="btn" disabled={saving}
                    onClick={() => { setCreating(false); setError(null); }}>Annuler</button>
            <button type="submit" className="btn primary" disabled={saving || !name.trim()}>
              {saving ? "Création..." : "Créer"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
